import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { getError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, User, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(form.username, form.password);
      navigate("/decisions");
    } catch (err) {
      setError(getError(err, "تعذر تسجيل الدخول"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="login-page">
      <div className="flex flex-1">
        <div className="hidden lg:flex w-1/2 bg-primary text-white flex-col items-center justify-center gap-8 p-12">
          <div className="bg-white rounded-2xl p-8 shadow-2xl">
            <img src="/logo-login.png" alt="سِجِل للأرشفة الإلكترونية" className="h-44 w-auto object-contain" />
          </div>
          <div className="text-center space-y-2">
            <h1 className="text-4xl font-extrabold tracking-tight">سِجِل للأرشفة الإلكترونية</h1>
            <p className="text-white/70 text-lg">نظام إدارة وأرشفة القرارات الإدارية</p>
          </div>
          <ul className="space-y-3 text-xl font-bold text-white" data-testid="login-features-list">
            <li className="flex items-center gap-3"><span className="text-accent text-2xl leading-none">•</span>سهولة الوصول للقرارات</li>
            <li className="flex items-center gap-3"><span className="text-accent text-2xl leading-none">•</span>حفظ وتنظيم الملفات</li>
            <li className="flex items-center gap-3"><span className="text-accent text-2xl leading-none">•</span>حماية المعلومات</li>
          </ul>
        </div>

        <div className="flex-1 flex items-center justify-center p-6">
          <div className="w-full max-w-md">
            <div className="flex justify-center mb-6">
              <img src="/logo-login.png" alt="سِجِل" className="h-28 w-auto object-contain" data-testid="login-form-logo" />
            </div>
            <div className="bg-white rounded-xl border border-border shadow-[0_4px_20px_-4px_rgba(15,61,62,0.08)] p-8">
              <h2 className="text-2xl font-bold text-primary mb-1">تسجيل الدخول</h2>
              <p className="text-muted-foreground text-sm mb-6">أدخل بياناتك للوصول إلى النظام</p>
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="username">اسم المستخدم</Label>
                  <div className="relative">
                    <User className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="username"
                      className="ps-10"
                      required
                      autoComplete="username"
                      value={form.username}
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      data-testid="login-username-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">كلمة المرور</Label>
                  <div className="relative">
                    <Lock className="absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      type="password"
                      className="ps-10"
                      required
                      autoComplete="current-password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      data-testid="login-password-input"
                    />
                  </div>
                </div>
                {error && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="login-error">
                    {error}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary hover:bg-secondary transition-colors duration-200 h-11 text-base font-bold"
                  data-testid="login-submit-btn"
                >
                  {loading && <Loader2 className="h-4 w-4 animate-spin ms-2" />}
                  دخول
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>

      <footer className="border-t border-border bg-white py-4 text-center text-sm text-muted-foreground" data-testid="login-footer">
        فكرة وتصميم الأستاذة/ جبرة الطويرقي
      </footer>
    </div>
  );
}
